import React, { useMemo } from 'react';
import Constants from 'relevant-shared/reportData/constants';
import { Badge } from '../Badge';
import { Link } from '../Link/Link';
import { dateRangeToString, getReportPreviewIcon, getReportTypeIcon } from '../ReportsOverview/utils';

export type ReportTemplate = {
	id: string;
	description?: string;
	settings: {
		type: string;
		title: string;
		showPieChart?: boolean;
		showTable?: boolean;
		showChart?: boolean;
		groupBy?: string;
	};
	hidden?: boolean;
	isAdminTemplate?: boolean;
	isUserTemplate?: boolean;
	isReportTemplate?: boolean;
	isDashboardTemplate?: boolean;
	userId?: string;
	createdDate?: string;
	isNew?: boolean;
	url: string;
	image: string;
	userName?: string;
	userPicture?: string;
	isAddedToDashboard?: boolean;
	appName?: string;
	appIcon?: React.ReactNode;
	icon?: React.ReactNode;
	badges?: string[];
};

export type ReportTemplateCardProps = {
	template?: ReportTemplate;
	skeleton?: boolean;
};

export function ReportTemplateCard({
	template,
	skeleton = false,
}: ReportTemplateCardProps) {
	const dateRange = useMemo(() => template && dateRangeToString(template.settings), [template]);

	return (
		<div className="flex flex-col bg-white rounded-lg border border-borders-primary hover:border-borders-primary_hover hover:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)] transition-shadow duration-50">
			<Link unstyled href={template?.url ?? '#'} className="flex flex-col gap-6 items-center p-6 pb-4 cursor-pointer group grow border-b border-borders-primary">
				<div className="flex flex-col w-full gap-2">
					<div className="flex flex-row justify-between gap-2">
						<div className="flex flex-col gap-2">
							<h3 className="text-text-primary text-md font-medium w-full">
								{skeleton ? (
									<span className="animate-pulse bg-grey-100 rounded h-6 w-3/4 block" />
								) : template?.settings.title}
							</h3>
							{skeleton ? (
								<span className="animate-pulse bg-grey-100 rounded h-5 w-24 block" />
							) : dateRange && (
								<Badge
									color={dateRange.relative ? 'blue' : 'grey'}
									className="self-start"
									onMouseDown={(e) => {
										// For debugging
										if (e.button === 2) {
											e.preventDefault();
											e.stopPropagation();
											console.log({ start: template.settings.start, end: template.settings.end, dynamicDateRange: template.settings.dynamicDateRange, settings: template.settings });
										}
									}}
								>
									{dateRange.text}
								</Badge>
							)}
						</div>
					</div>
					{skeleton ? (
						<span className="animate-pulse bg-grey-100 rounded h-4 w-full block mb-2" />
					) : (
						<p className="text-sm text-text-secondary mb-2">
							{template?.description}
						</p>
					)}
				</div>
			</Link>
			<div className="flex items-center justify-between text-sm text-grey-650 px-6 py-[14px] gap-2 min-w-0 overflow-hidden">
				<div className="flex gap-[10px] items-center shrink min-w-0">
					{skeleton ? (
						<>
							<div className="animate-pulse bg-grey-100 rounded size-5" />
							<span className="animate-pulse bg-grey-100 rounded h-4 w-24 block" />
						</>
					) : (
						<>
							<img src={getReportTypeIcon(template?.settings.type)} alt={Constants[template?.settings.type].DESCRIPTION} className="size-5!" />
							<span className="text-sm text-text-secondary font-semibold leading-5 truncate">{Constants[template?.settings.type].DESCRIPTION}</span>
						</>
					)}
				</div>
				<div className="flex items-center justify-center gap-4">
					{skeleton ? (
						<div className="animate-pulse bg-grey-100 rounded size-[20px] m-1" />
					) : (
						<img src={getReportPreviewIcon(template?.settings)} className="size-[20px] m-1" />
					)}
				</div>
			</div>
		</div>
	);
}
